// scene-core/walk-geometry.ts — pure, dependency-free math for the walking-path
// feature. THE single source of truth: imported by the editor (authoring
// overlay + walk preview + scene.json export) and viewer2 (camera height +
// boundary clamp). Both `webpages/editor/src/walk-geometry.ts` and
// `webpages/viewer2/src/walk-geometry.ts` are thin re-export shims of this file, so the
// three consumers — editor authoring, editor walk-preview, viewer walk — can
// never drift.
//
// Intentionally NO playcanvas / DOM imports so it stays unit-testable offline
// and portable across both build systems (rollup + vite). All inputs/outputs
// are plain tuples/numbers.
//
// Coordinate model:
//   - boundary is a closed polygon in world XZ (no repeated last point).
//   - floor is a plane (origin + upward normal); it governs camera HEIGHT only,
//     so a slightly tilted floor is handled while the XZ clamp stays 2D.

export type Vec2 = [number, number];          // world XZ
export type Vec3 = [number, number, number];  // world XYZ

export interface Plane {
    origin: Vec3;  // a point on the plane (first floor click)
    normal: Vec3;  // unit normal, forced to point up (+Y)
}

/**
 * Canonical default standing eye height (metres) when a scene doesn't carry an
 * explicit `eyeHeight`. ONE value for authoring, preview, and the viewer — they
 * used to disagree (editor 1.75 vs viewer 1.6), which made a scene with no
 * explicit eye height walk at different heights depending on where you viewed
 * it. ~1.6 m is the standard average standing eye height.
 */
export const CANONICAL_EYE_HEIGHT = 1.6;

/**
 * Even-odd ray-cast point-in-polygon on the XZ plane.
 * `poly` is the closed boundary as [x, z] pairs (do not repeat the first point).
 */
export const pointInPolygon = (x: number, z: number, poly: Vec2[]): boolean => {
    let inside = false;
    const n = poly.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        const xi = poly[i][0], zi = poly[i][1];
        const xj = poly[j][0], zj = poly[j][1];
        const intersects = ((zi > z) !== (zj > z)) &&
            (x < ((xj - xi) * (z - zi)) / ((zj - zi) || 1e-12) + xi);
        if (intersects) inside = !inside;
    }
    return inside;
};

/**
 * Nearest point on the polygon OUTLINE (projection onto the closest edge),
 * whether (x, z) is inside or outside. Stairs authoring uses this to seat
 * flight corners exactly on a walk area's side.
 */
export const nearestOnPolygonEdge = (x: number, z: number, poly: Vec2[]): { x: number; z: number } => {
    let bestX = x, bestZ = z, bestD = Infinity;
    const n = poly.length;
    for (let i = 0; i < n; i++) {
        const ax = poly[i][0], az = poly[i][1];
        const bx = poly[(i + 1) % n][0], bz = poly[(i + 1) % n][1];
        const dx = bx - ax, dz = bz - az;
        const lenSq = dx * dx + dz * dz;
        const t = lenSq > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / lenSq)) : 0;
        const px = ax + dx * t, pz = az + dz * t;
        const d = (px - x) * (px - x) + (pz - z) * (pz - z);
        if (d < bestD) { bestD = d; bestX = px; bestZ = pz; }
    }
    return { x: bestX, z: bestZ };
};

/**
 * If (x, z) is outside `poly`, return the nearest point on the polygon
 * boundary (projection onto the closest edge); otherwise return (x, z)
 * unchanged. This is the camera clamp used every frame by the viewer.
 */
export const clampToBoundary = (x: number, z: number, poly: Vec2[]): { x: number; z: number } => {
    if (!poly || poly.length < 3) return { x, z };
    if (pointInPolygon(x, z, poly)) return { x, z };
    return nearestOnPolygonEdge(x, z, poly);
};

/** True when 3 points are (near-)collinear and can't define a stable plane. */
export const isDegeneratePlane = (a: Vec3, b: Vec3, c: Vec3, eps = 1e-4): boolean => {
    const abx = b[0] - a[0], aby = b[1] - a[1], abz = b[2] - a[2];
    const acx = c[0] - a[0], acy = c[1] - a[1], acz = c[2] - a[2];
    const nx = aby * acz - abz * acy;
    const ny = abz * acx - abx * acz;
    const nz = abx * acy - aby * acx;
    return Math.hypot(nx, ny, nz) < eps;
};

/**
 * Build a plane from 3 non-collinear points. The normal is normalised and
 * forced to point up (+Y) so the viewer can offset eye height along it.
 * (Height math is sign-independent, so the flip is purely for the viewer.)
 */
export const planeFrom3 = (a: Vec3, b: Vec3, c: Vec3): Plane => {
    const abx = b[0] - a[0], aby = b[1] - a[1], abz = b[2] - a[2];
    const acx = c[0] - a[0], acy = c[1] - a[1], acz = c[2] - a[2];
    let nx = aby * acz - abz * acy;
    let ny = abz * acx - abx * acz;
    let nz = abx * acy - aby * acx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    if (ny < 0) { nx = -nx; ny = -ny; nz = -nz; }
    return { origin: [a[0], a[1], a[2]], normal: [nx, ny, nz] };
};

/** A horizontal plane at world height y (the trivial / un-tilted floor). */
export const horizontalPlane = (y: number): Plane => ({ origin: [0, y, 0], normal: [0, 1, 0] });

/**
 * World Y of the plane directly above/below (x, z). Falls back to the origin
 * height for near-vertical planes (which shouldn't occur for a floor).
 */
export const planeHeightAt = (plane: Plane, x: number, z: number): number => {
    const o = plane.origin, n = plane.normal;
    if (Math.abs(n[1]) < 1e-6) return o[1];
    return o[1] - (n[0] * (x - o[0]) + n[2] * (z - o[2])) / n[1];
};

/**
 * Intersect a ray (origin `ro`, direction `rd`, need not be normalised) with
 * the plane. Returns the world hit point, or null if parallel / behind origin.
 * Used by the editor tool to drop boundary vertices onto the authored floor.
 */
export const rayPlane = (ro: Vec3, rd: Vec3, plane: Plane): Vec3 | null => {
    const o = plane.origin, n = plane.normal;
    const denom = rd[0] * n[0] + rd[1] * n[1] + rd[2] * n[2];
    if (Math.abs(denom) < 1e-9) return null;
    const t = ((o[0] - ro[0]) * n[0] + (o[1] - ro[1]) * n[1] + (o[2] - ro[2]) * n[2]) / denom;
    if (t <= 0) return null;
    return [ro[0] + rd[0] * t, ro[1] + rd[1] * t, ro[2] + rd[2] * t];
};

// ═══════════════════════════════════════════════════════════════════════════
// Multi-level walking — several floors at different heights, connected by
// stair regions. Everything below reduces to a flat list of SURFACES (plane +
// XZ polygon): the ground floor, each extra level (horizontal plane at its
// height) and each stairs quad (a planar ramp). One resolve function then
// answers, per frame, "where may the camera stand and how high is the floor
// there" — shared verbatim by the editor walk-preview and viewer2.
// ═══════════════════════════════════════════════════════════════════════════

export interface WalkSurface {
    plane: Plane;
    poly: Vec2[];              // walkable footprint in world XZ
    kind: 'level' | 'stairs';
}

/** A resolved standing position: XZ (possibly clamped) + floor height there. */
export interface WalkPoint {
    x: number;
    z: number;
    y: number;                 // FLOOR height (feet), not eye height
    surface: number;           // index into the surfaces array (-1 if none)
}

/**
 * Max height difference (metres) the camera may step across without stairs.
 * Governs both walking onto an adjacent surface (stairs edge → floor) and
 * which overlapping surfaces count as "reachable" — floors further apart than
 * this act as separate storeys with invisible walls between them.
 */
export const STEP_TOLERANCE = 0.45;

/** Signed area of an XZ polygon (shoelace). Near-zero → degenerate/bowtie. */
export const polygonArea = (poly: Vec2[]): number => {
    let a = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        a += (poly[j][0] * poly[i][1]) - (poly[i][0] * poly[j][1]);
    }
    return a / 2;
};

/**
 * Order 4 stair corners into a SIMPLE quad [low0, low1, highA, highB]: keeps
 * the low pair in place and swaps the high pair if that ordering would
 * self-intersect (the bowtie has the smaller absolute shoelace area).
 */
export const orderStairsQuad = (low0: Vec2, low1: Vec2, highA: Vec2, highB: Vec2): Vec2[] => {
    const q1: Vec2[] = [low0, low1, highA, highB];
    const q2: Vec2[] = [low0, low1, highB, highA];
    return Math.abs(polygonArea(q1)) >= Math.abs(polygonArea(q2)) ? q1 : q2;
};

/**
 * The planar ramp surface of a stairs quad: height `low` along the edge
 * quad[0]→quad[1], `high` along quad[2]→quad[3], varying linearly along the
 * axis between the two edge midpoints and constant across it. Expressed as a
 * Plane so stairs reuse the exact same heightAt / raycast code as floors.
 */
export const stairsPlane = (quad: Vec2[], low: number, high: number): Plane => {
    const mlx = (quad[0][0] + quad[1][0]) / 2, mlz = (quad[0][1] + quad[1][1]) / 2;
    const mhx = (quad[2][0] + quad[3][0]) / 2, mhz = (quad[2][1] + quad[3][1]) / 2;
    const ux = mhx - mlx, uz = mhz - mlz;         // horizontal climb axis
    const axisSq = ux * ux + uz * uz;
    if (axisSq < 1e-10) return horizontalPlane((low + high) / 2);
    const uy = high - low;
    // normal of the plane spanned by (ux, uy, uz) and the level lateral
    // (-uz, 0, ux); ny = axisSq > 0 so it already points up.
    let nx = -ux * uy, ny = axisSq, nz = -uz * uy;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    return { origin: [mlx, low, mlz], normal: [nx, ny, nz] };
};

/** Structural (dependency-free) view of scene.json's `walk` block. */
export interface WalkConfigLike {
    floor: { origin: number[]; normal: number[] };
    boundary: number[][];
    levels?: { height: number; boundary: number[][] }[];
    stairs?: { quad: number[][]; lowHeight: number; highHeight: number }[];
}

const toVec2Poly = (pts: number[][]): Vec2[] =>
    pts.filter(p => Array.isArray(p) && p.length >= 2).map(p => [p[0], p[1]] as Vec2);

/**
 * Flatten a walk config into the surface list the resolver runs on. Index 0
 * is always the ground floor (the legacy `floor` plane + `boundary` polygon);
 * invalid/incomplete levels and stairs are skipped. A config without
 * `levels`/`stairs` yields exactly one surface — the legacy single-floor case.
 */
export const buildWalkSurfaces = (walk: WalkConfigLike): WalkSurface[] => {
    const surfaces: WalkSurface[] = [];
    const ground = toVec2Poly(walk.boundary ?? []);
    if (ground.length >= 3 && walk.floor?.origin && walk.floor?.normal) {
        surfaces.push({
            plane: {
                origin: [walk.floor.origin[0], walk.floor.origin[1], walk.floor.origin[2]],
                normal: [walk.floor.normal[0], walk.floor.normal[1], walk.floor.normal[2]]
            },
            poly: ground,
            kind: 'level'
        });
    }
    for (const l of walk.levels ?? []) {
        if (!Number.isFinite(l?.height)) continue;
        const poly = toVec2Poly(l.boundary ?? []);
        if (poly.length < 3) continue;
        surfaces.push({ plane: horizontalPlane(l.height), poly, kind: 'level' });
    }
    for (const s of walk.stairs ?? []) {
        if (!Number.isFinite(s?.lowHeight) || !Number.isFinite(s?.highHeight)) continue;
        const quad = toVec2Poly(s.quad ?? []);
        if (quad.length !== 4) continue;
        surfaces.push({ plane: stairsPlane(quad, s.lowHeight, s.highHeight), poly: quad, kind: 'stairs' });
    }
    return surfaces;
};

/**
 * Per-frame movement resolve: given the previous FLOOR height under the
 * camera (`prevFeetY`) and the wished-for XZ, return where the camera may
 * actually stand. A surface is reachable when its height at the target is
 * within STEP_TOLERANCE of the previous floor height — so you follow a stair
 * ramp continuously, step over small level differences, and are walled off
 * from floors far above/below even where their footprints overlap in XZ.
 * Blocked moves clamp to the nearest boundary point among reachable surfaces
 * (the multi-surface generalisation of the old clampToBoundary).
 * `tolerance` widens the reachable band; voxel walking (voxel-walk.ts) gates
 * against the real floor with a little slack for floors that are not flat.
 */
export const resolveWalkPoint = (
    surfaces: WalkSurface[], prevFeetY: number, x: number, z: number, tolerance = STEP_TOLERANCE
): WalkPoint => {
    if (surfaces.length === 0) return { x, z, y: Number.isFinite(prevFeetY) ? prevFeetY : 0, surface: -1 };

    // 1) Surfaces containing the target at a reachable height. A stair ramp
    //    in reach beats a level: floor outlines are usually drawn right over
    //    the stairs, and the flat floor (always the closer height) would
    //    otherwise win every step, so the ramp was never climbed. Under a
    //    staircase or beside its high end the ramp is out of reach, so the
    //    floor still wins there.
    let best = -1, bestDy = Infinity, bestStairs = false;
    for (let i = 0; i < surfaces.length; i++) {
        const s = surfaces[i];
        if (!pointInPolygon(x, z, s.poly)) continue;
        const h = planeHeightAt(s.plane, x, z);
        const dy = Math.abs(h - prevFeetY);
        if (dy > tolerance) continue;
        const stairs = s.kind === 'stairs';
        if ((stairs && !bestStairs) || (stairs === bestStairs && dy < bestDy)) {
            best = i; bestDy = dy; bestStairs = stairs;
        }
    }
    if (best >= 0) return { x, z, y: planeHeightAt(surfaces[best].plane, x, z), surface: best };

    // 2) Blocked: clamp to the nearest reachable boundary point.
    let cx = x, cz = z, cy = prevFeetY, cs = -1, bestD = Infinity;
    for (let i = 0; i < surfaces.length; i++) {
        const s = surfaces[i];
        const c = clampToBoundary(x, z, s.poly);
        const h = planeHeightAt(s.plane, c.x, c.z);
        if (Math.abs(h - prevFeetY) > tolerance) continue;
        const d = (c.x - x) * (c.x - x) + (c.z - z) * (c.z - z);
        if (d < bestD) { bestD = d; cx = c.x; cz = c.z; cy = h; cs = i; }
    }
    if (cs >= 0) return { x: cx, z: cz, y: cy, surface: cs };

    // 3) Nothing within stepping range (bad prevFeetY / teleport): re-seed.
    return seedWalkPoint(surfaces, x, z, prevFeetY);
};

/**
 * Place the camera with no valid previous height (spawn, teleport): stand on
 * the surface under (x, z) whose height is closest to `preferY` (NaN → the
 * ground floor / first hit), clamping into the nearest surface when (x, z)
 * is outside all of them.
 */
export const seedWalkPoint = (surfaces: WalkSurface[], x: number, z: number, preferY: number): WalkPoint => {
    if (surfaces.length === 0) return { x, z, y: Number.isFinite(preferY) ? preferY : 0, surface: -1 };
    const hasPrefer = Number.isFinite(preferY);

    let best = -1, bestDy = Infinity;
    for (let i = 0; i < surfaces.length; i++) {
        const s = surfaces[i];
        if (!pointInPolygon(x, z, s.poly)) continue;
        if (!hasPrefer) { best = i; break; }  // first (ground-most) surface wins
        const dy = Math.abs(planeHeightAt(s.plane, x, z) - preferY);
        if (dy < bestDy) { best = i; bestDy = dy; }
    }
    if (best >= 0) return { x, z, y: planeHeightAt(surfaces[best].plane, x, z), surface: best };

    let cx = x, cz = z, cy = 0, cs = 0, bestD = Infinity, bestDh = Infinity;
    for (let i = 0; i < surfaces.length; i++) {
        const s = surfaces[i];
        const c = clampToBoundary(x, z, s.poly);
        const h = planeHeightAt(s.plane, c.x, c.z);
        const d = (c.x - x) * (c.x - x) + (c.z - z) * (c.z - z);
        const dh = hasPrefer ? Math.abs(h - preferY) : 0;
        if (d < bestD - 1e-9 || (Math.abs(d - bestD) <= 1e-9 && dh < bestDh)) {
            bestD = d; bestDh = dh; cx = c.x; cz = c.z; cy = h; cs = i;
        }
    }
    return { x: cx, z: cz, y: cy, surface: cs };
};

/**
 * Nearest walk surface under a camera ray — hover indicator + click-to-move
 * across floors. Only top-side hits count (ray descending through the
 * surface), so aiming at the UNDERSIDE of an upper floor from below never
 * targets it. Returns the world hit point on the winning surface, or null.
 */
export const raycastWalkSurfaces = (
    ro: Vec3, rd: Vec3, surfaces: WalkSurface[]
): { point: Vec3; surface: number } | null => {
    let best: { point: Vec3; surface: number } | null = null;
    let bestD = Infinity;
    for (let i = 0; i < surfaces.length; i++) {
        const s = surfaces[i];
        const n = s.plane.normal;
        if (rd[0] * n[0] + rd[1] * n[1] + rd[2] * n[2] >= 0) continue; // back side
        const hit = rayPlane(ro, rd, s.plane);
        if (!hit || !pointInPolygon(hit[0], hit[2], s.poly)) continue;
        const dx = hit[0] - ro[0], dy = hit[1] - ro[1], dz = hit[2] - ro[2];
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bestD) { bestD = d; best = { point: hit, surface: i }; }
    }
    return best;
};
